import { Box, Stack } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import type { TFetchError } from '@/api/api-error.types';
import { _deleteMemberOrInvitation, _postInvitation } from '@/api/entity/mutations';
import { ENTITY_QUERY_KEYS } from '@/api/entity/queries';
import { SortDirection, type SortDirectionType } from '@/api/types';
import PlusIcon from '@/assets/svg/ui-elements/plus.svg';
import TrashIcon from '@/assets/svg/ui-elements/trash-2.svg';
import UsersIcon from '@/assets/svg/ui-elements/users.svg';
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  getTableMinWidth,
  Helper,
  IconButton,
  Link,
  Table,
  type TableColumn,
  theme,
  Tooltip,
  Typography,
  useToast,
} from '@/design-system';
import { PagePaths } from '@/routes/page-paths';
import { useEntitySlice } from '@/stores/selectors';
import { getApiErrorMessage } from '@/utils/api-error-message';

import {
  DIRECTOR_MINIMUM_MET,
  DIRECTORS_EMPTY_DESCRIPTION,
  DIRECTORS_EMPTY_TITLE,
  directorStatusBadges,
} from '../common';
import { DirectorHeadshot, FormPanel, SectionHeading } from '../styled';
import { useCreateEntity } from '../use-create-entity';
import { useEntityReviewMode } from '../use-entity-review-mode';
import { useEntityWriteAccess } from '../use-entity-write-access';
import { getStepFieldErrors } from '../validate-step';
import { useWizardTitleSlot } from '../wizard-title-slot';
import { AddDirectorModal } from './add-director-modal';
import { sortDirectorRows } from './sort-director-rows';
import { type DirectorRow, useDirectorRows } from './use-director-rows';
import { UpdatedBadge } from './wizard-status-badges';

export const ListDirectors = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [serverError, setServerError] = useState<string>();
  const { stepForms, updateStepForm, validatedSteps, currentStep } = useEntitySlice();
  const { isEntityApprovalStatusVerified, filteredDirectors } = useCreateEntity();
  const { canAddDirector, canRemoveMember, canRemoveInvitation } = useEntityWriteAccess();
  const { isReviewMode, isDirectorChanged, isDirectorsStepChanged, highlightIf } =
    useEntityReviewMode();
  const { rows, isLoading } = useDirectorRows(stepForms.id);
  const titleSlot = useWizardTitleSlot();
  const { add: addToast } = useToast();
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirectionType | null>(null);

  const sortedRows = useMemo(
    () => sortDirectorRows(rows, sortKey, sortDirection),
    [rows, sortKey, sortDirection],
  );

  const reviewRows = useMemo(
    () =>
      sortedRows.map((row) => ({
        ...row,
        changed: isReviewMode && (row.isMember ? isDirectorChanged(row.userId) : true),
      })),
    [sortedRows, isReviewMode, isDirectorChanged],
  );

  const handleSort = (key: string) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection(SortDirection.ASC);
      return;
    }

    if (sortDirection === SortDirection.ASC) {
      setSortDirection(SortDirection.DESC);
      return;
    }

    setSortKey(null);
    setSortDirection(null);
  };

  const { mutate, isPending } = useMutation({ mutationFn: _postInvitation });
  const { mutate: deleteMemberOrInvitation } = useMutation({
    mutationFn: _deleteMemberOrInvitation,
  });

  const isStepValidated = validatedSteps.includes(currentStep);
  const fieldErrors = getStepFieldErrors(currentStep, stepForms);
  const isValid = !isStepValidated || !fieldErrors['members.director'];

  const members = stepForms.members;
  const invitations = stepForms.invitations;

  const existingEmails = [...invitations, ...members]
    .map((entry) => ('email' in entry ? entry.email : ''))
    .filter(Boolean);

  const handleAddDirector = (email: string) => {
    const invitation = {
      email,
      title: 'Team Member',
      role: 'Viewer',
      legalRole: 'Director',
    } as const;

    setServerError(undefined);

    mutate(
      { entityId: stepForms.id, ...invitation },
      {
        onSuccess: async ({ id }) => {
          const entityQuery = { queryKey: [ENTITY_QUERY_KEYS.entities, stepForms.id] };
          // Without cached data, invalidation reuses an initial read taken before the invitation.
          await queryClient.cancelQueries(entityQuery);
          updateStepForm({
            invitations: [...invitations, { ...invitation, id }],
          });
          queryClient.invalidateQueries(entityQuery);
          setIsModalOpen(false);
          addToast({
            title: 'Invitation sent',
            description: `${email} was invited as a director.`,
            data: { status: 'success' },
          });
        },
        onError: (error) => {
          setServerError(getApiErrorMessage(error as TFetchError, 'Could not send the invitation'));
        },
      },
    );
  };

  const handleRemoveInvitationOrMember = (id: string, isMember: boolean) => {
    deleteMemberOrInvitation(
      { entityId: stepForms.id, memberOrInvitationId: id },
      {
        onSuccess: () => {
          updateStepForm(
            isMember
              ? { members: members.filter((el) => el.id !== id) }
              : { invitations: invitations.filter((el) => el.id !== id) },
          );
        },
      },
    );
  };

  const columns: TableColumn<DirectorRow>[] = [
    {
      key: 'director',
      sortable: true,
      header: 'Director',
      minWidth: 200,
      cell: (row) => (
        <Stack alignItems="center" gap={`${theme.spacing[8]}px`}>
          {row.headshotImageUrl ? (
            <DirectorHeadshot src={row.headshotImageUrl} alt="" />
          ) : row.name ? (
            <Avatar size="sm" initials={row.name} />
          ) : (
            <Avatar size="sm" type="icon" />
          )}
          <Typography variant="body3" noWrap>
            {row.name || '—'}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'email',
      sortable: true,
      header: 'Email',
      minWidth: 220,
      cell: (row) => (
        <Typography variant="body3" noWrap>
          {row.email}
        </Typography>
      ),
    },
    {
      key: 'invitation',
      sortable: true,
      header: 'Invitation',
      minWidth: 180,
      cell: (row) => {
        const badge = directorStatusBadges[row.status];

        return badge.info ? (
          <Tooltip side="top" title={badge.info}>
            <Box display="inline-flex">
              <Badge label={badge.label} color={badge.color} variant="subtle" />
            </Box>
          </Tooltip>
        ) : (
          <Badge label={badge.label} color={badge.color} variant="subtle" />
        );
      },
    },
    {
      key: 'actions',
      header: '',
      width: 56,
      align: 'right',
      cell: (row) => (
        <IconButton
          aria-label={`Remove ${row.email || row.name}`}
          variant="outline"
          color="destructive"
          size="sm"
          disabled={
            isReviewMode ||
            isEntityApprovalStatusVerified ||
            (row.isMember ? !canRemoveMember : !canRemoveInvitation)
          }
          onClick={() => handleRemoveInvitationOrMember(row.id, row.isMember)}
        >
          <TrashIcon />
        </IconButton>
      ),
    },
  ];

  const addDirectorButton = (
    <Button
      size="md"
      disabled={isEntityApprovalStatusVerified || !canAddDirector || isReviewMode}
      onClick={() => {
        setServerError(undefined);
        setIsModalOpen(true);
      }}
    >
      <PlusIcon />
      Add director
    </Button>
  );

  return (
    <>
      {titleSlot && createPortal(addDirectorButton, titleSlot)}

      <FormPanel $changed={highlightIf(isDirectorsStepChanged)}>
        {isDirectorsStepChanged && (
          <SectionHeading>
            <UpdatedBadge />
          </SectionHeading>
        )}

        {isEntityApprovalStatusVerified && (
          <Typography variant="body3" color={theme.colors.text.secondary}>
            Directors can no longer be edited here. Please use the{' '}
            <Link to={PagePaths.manageTeam} size="small">
              Team Management Dashboard
            </Link>{' '}
            to make changes.
          </Typography>
        )}

        <Table
          columns={columns}
          rows={reviewRows}
          isLoading={isLoading}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          minTableWidth={getTableMinWidth(columns)}
          emptyState={
            <EmptyState
              icon={<UsersIcon />}
              title={DIRECTORS_EMPTY_TITLE}
              description={DIRECTORS_EMPTY_DESCRIPTION}
            />
          }
        />

        {filteredDirectors.length ? (
          <Helper label={DIRECTOR_MINIMUM_MET} iconStart={false} />
        ) : (
          !isValid && <Helper label="At least one director is required" state="destructive" />
        )}

        <AddDirectorModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          existingEmails={existingEmails}
          isSending={isPending}
          serverError={serverError}
          onSubmit={handleAddDirector}
          onEmailChange={() => setServerError(undefined)}
        />
      </FormPanel>
    </>
  );
};
